import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import WelcomePage from './pages/WelcomePage';
import DetectPage from './pages/DetectPage';
import FeedPage from './pages/FeedPage';
import DealPage from './pages/DealPage';
import DealPreviewPage from './pages/DealPreviewPage';
import QRPage from './pages/QRPage';
import SuccessPage from './pages/SuccessPage';
import JourneyPage from './pages/JourneyPage';
import MapPage from './pages/MapPage';
import SettingsPage from './pages/SettingsPage';

import ShopLoginPage from './pages/shop/ShopLoginPage';
import ShopDashboardPage from './pages/shop/ShopDashboardPage';
import ShopDealsPage from './pages/shop/ShopDealsPage';
import ShopNewDealPage from './pages/shop/ShopNewDealPage';
import ShopScanPage from './pages/shop/ShopScanPage';
import ShopStatsPage from './pages/shop/ShopStatsPage';
import ShopTokensPage from './pages/shop/ShopTokensPage';
import ShopCheckoutPage from './pages/shop/ShopCheckoutPage';
import ShopSignupPage from './pages/shop/ShopSignupPage';
import ShopProfilePage from './pages/shop/ShopProfilePage';
import ShopForgotPasswordPage from './pages/shop/ShopForgotPasswordPage';
import ShopResetPasswordPage from './pages/shop/ShopResetPasswordPage';
import ShopEditDealPage from './pages/shop/ShopEditDealPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import AdminLayout from './pages/admin/AdminLayout';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminShopsPage from './pages/admin/AdminShopsPage';
import AdminShopDetailPage from './pages/admin/AdminShopDetailPage';
import AdminNewShopPage from './pages/admin/AdminNewShopPage';
import AdminDealsPage from './pages/admin/AdminDealsPage';
import AdminInvoicesPage from './pages/admin/AdminInvoicesPage';
import AdminTokensPage from './pages/admin/AdminTokensPage';
import AdminPackagesPage from './pages/admin/AdminPackagesPage';
import AdminStatsPage from './pages/admin/AdminStatsPage';
import AdminAreasPage from './pages/admin/AdminAreasPage';
import AdminAreaEditPage from './pages/admin/AdminAreaEditPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';

// One-time migrations on app load:
//  1. walkin_shop_deals moved from sessionStorage to localStorage
//  2. backfill createdBy on existing deals (older deals only stored shopName)
(function migrateShopDeals() {
  try {
    const oldDeals = sessionStorage.getItem('walkin_shop_deals');
    if (oldDeals) {
      const existing = JSON.parse(localStorage.getItem('walkin_shop_deals') || '[]');
      const parsed = JSON.parse(oldDeals);
      if (Array.isArray(parsed) && parsed.length > 0) {
        const seen = new Set(existing.map((d) => String(d.id)));
        const merged = [
          ...existing,
          ...parsed.filter((d) => !seen.has(String(d.id))),
        ];
        localStorage.setItem('walkin_shop_deals', JSON.stringify(merged));
        console.log('[WalkIn] Migrated', parsed.length, 'deals to localStorage');
      }
      sessionStorage.removeItem('walkin_shop_deals');
    }

    // Backfill createdBy = shopName for older records
    const raw = localStorage.getItem('walkin_shop_deals');
    if (raw) {
      const list = JSON.parse(raw);
      let touched = false;
      const next = list.map((d) => {
        if (!d.createdBy && d.shopName) {
          touched = true;
          return { ...d, createdBy: d.shopName };
        }
        return d;
      });
      if (touched) {
        localStorage.setItem('walkin_shop_deals', JSON.stringify(next));
        console.log('[WalkIn] Backfilled createdBy on', next.length, 'deals');
      }
    }
  } catch (e) {
    console.warn('[WalkIn] Deal migration failed:', e);
  }
})();

// One-time demo-data purge when the app bumps versions.
// The walker flow now reads deals exclusively from the backend, so anything
// left in the local deal store is stale demo/offline data from an older build
// and is cleared outright rather than filtered.
(function purgeDemoDataOnVersionBump() {
  const APP_VERSION = '3.0.0';
  try {
    const storedVersion = localStorage.getItem('walkin_app_version');
    if (storedVersion === APP_VERSION) return;

    const shops = JSON.parse(localStorage.getItem('walkin_shops') || '[]');
    localStorage.setItem(
      'walkin_shops',
      JSON.stringify(shops.filter((s) => !s.isDemo))
    );

    localStorage.removeItem('walkin_shop_deals');
    localStorage.removeItem('walkin_admin_shops');
    localStorage.removeItem('walkin_admin_invoices');
    sessionStorage.removeItem('walkin_demo_mode');
    sessionStorage.removeItem('walkin_demo_area');
    localStorage.setItem('walkin_app_version', APP_VERSION);
  } catch (e) {
    console.warn('[WalkIn] Demo purge failed:', e);
  }
})();

function ShopProtected({ children }) {
  const isShopMode = sessionStorage.getItem('walkin_shop_mode') === 'true';
  if (!isShopMode) return <Navigate to="/shop/login" replace />;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<WelcomePage />} />
        <Route path="/detect" element={<DetectPage />} />
        <Route path="/feed" element={<FeedPage />} />
        <Route path="/deal/:id" element={<DealPage />} />
        <Route path="/deal-preview/:id" element={<DealPreviewPage />} />
        <Route path="/qr/:id" element={<QRPage />} />
        <Route path="/success/:id" element={<SuccessPage />} />
        <Route path="/journey" element={<JourneyPage />} />
        <Route path="/map" element={<MapPage />} />
        <Route path="/settings" element={<SettingsPage />} />

        <Route path="/shop/login" element={<ShopLoginPage />} />
        <Route path="/shop/dashboard" element={<ShopProtected><ShopDashboardPage /></ShopProtected>} />
        <Route path="/shop/deals" element={<ShopProtected><ShopDealsPage /></ShopProtected>} />
        <Route path="/shop/new-deal" element={<ShopProtected><ShopNewDealPage /></ShopProtected>} />
        <Route path="/shop/edit-deal/:id" element={<ShopProtected><ShopEditDealPage /></ShopProtected>} />
        <Route path="/shop/scan" element={<ShopProtected><ShopScanPage /></ShopProtected>} />
        <Route path="/shop/stats" element={<ShopProtected><ShopStatsPage /></ShopProtected>} />
        <Route path="/shop/tokens" element={<ShopProtected><ShopTokensPage /></ShopProtected>} />
        <Route path="/shop/checkout" element={<ShopProtected><ShopCheckoutPage /></ShopProtected>} />
        <Route path="/shop/signup" element={<ShopSignupPage />} />
        <Route path="/shop/profile" element={<ShopProtected><ShopProfilePage /></ShopProtected>} />
        <Route path="/shop/forgot-password" element={<ShopForgotPasswordPage />} />
        <Route path="/shop/reset-password" element={<ShopResetPasswordPage />} />

        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />

        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboardPage />} />
          <Route path="dashboard" element={<AdminDashboardPage />} />
          <Route path="shops" element={<AdminShopsPage />} />
          <Route path="shops/new" element={<AdminNewShopPage />} />
          <Route path="shops/:id" element={<AdminShopDetailPage />} />
          <Route path="deals" element={<AdminDealsPage />} />
          <Route path="invoices" element={<AdminInvoicesPage />} />
          <Route path="tokens" element={<AdminTokensPage />} />
          <Route path="packages" element={<AdminPackagesPage />} />
          <Route path="stats" element={<AdminStatsPage />} />
          <Route path="areas" element={<AdminAreasPage />} />
          <Route path="areas/new" element={<AdminAreaEditPage />} />
          <Route path="areas/:id/edit" element={<AdminAreaEditPage />} />
          <Route path="settings" element={<AdminSettingsPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
